/**
 * Thin API client for the ingest pipeline.
 *
 * Wraps the global apiClient so tests can stub this module without
 * touching the real fetch/auth logic. Real backend wires in during P2 Group A.
 */

import { apiClient, API_BASE } from '../../lib/api-client'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface BatchCreateResponse {
  batchId: string
}

/** Optional package context carried on batch creation (GH-252). */
export interface BatchCreatePayload {
  label?: string
  packageId?: string
  sourceGroup?: string
}

export interface BatchItemPayload {
  uploadId: string
  filename: string
  mime: string
  size: number
}

export interface BatchAddItemsPayload {
  uploads: BatchItemPayload[]
}

/** Server-sent event payload shapes for `GET /api/ingest/batches/:id/stream` */
export type BatchItemStatus = 'queued' | 'extracting' | 'ocr-retry' | 'ready' | 'failed'

export interface BatchItemEvent {
  uploadId: string
  filename: string
  status: BatchItemStatus
  error?: string
}

export interface DirectUploadResponse {
  uploadId: string
  /** Pre-signed URL for multipart init (files >= 100 MB use multipart) */
  url?: string
}

export interface MultipartInitResponse {
  uploadId: string
  uploadUrl: string
  key: string
}

export interface MultipartCompletePayload {
  uploadId: string
  key: string
  parts: { partNumber: number; etag: string }[]
}

// OCR Settings admin
export interface OcrProviderSetting {
  id: string
  provider: string
  label: string
  apiKey: string // masked on read: '••••••••'
  isDefault: boolean
  /** For custom-endpoint only — base URL (e.g. http://ollama:11434). #195 */
  endpoint?: string
  /** For custom-endpoint only — provider-side model name. #195 */
  model?: string
  /** For google-document-ai only — full processor path. #195 */
  processorPath?: string
  createdAt: string
  updatedAt: string
}

export interface OcrSettingsListResponse {
  settings: OcrProviderSetting[]
}

export interface OcrSettingCreatePayload {
  provider: string
  label: string
  apiKey: string
  isDefault?: boolean
  /** For custom-endpoint only — base URL (e.g. http://ollama:11434) */
  endpoint?: string
  /** For custom-endpoint only — provider-side model name (e.g. glm-ocr:latest) */
  model?: string
  /** For google-document-ai only — projects/.../processors/... */
  processorPath?: string
}

export interface OcrSettingUpdatePayload {
  label?: string
  apiKey?: string
  isDefault?: boolean
  endpoint?: string
  model?: string
  processorPath?: string
}

// ---------------------------------------------------------------------------
// P4 types (review queue)
// ---------------------------------------------------------------------------

export interface BatchItemsQuery {
  /** Array of status values — sent as repeated ?status= params (F3 fix). */
  status?: string[]
  /** Array of errorClass values — sent as repeated ?errorClass= params (F3 fix). */
  errorClass?: string[]
  language?: string
  size?: string
  search?: string
  page?: number
  pageSize?: number
}

export interface BatchItemsResponse {
  items: Record<string, unknown>[]
  total: number
  page: number
  pageSize: number
}

export interface BatchStatsResponse {
  counts: Record<string, number>
  byErrorClass: Record<string, number>
  total: number
}

export interface BulkActionPayload {
  action: 'accept' | 'reject' | 'retry' | 're-extract' | 'assign-folder' | 'add-tags'
  itemIds: string[]
  params?: {
    providerId?: string
    folderId?: string
    folderPath?: string
    tags?: string[]
  }
}

export interface BulkActionResult {
  accepted: number
  failed: Array<{ itemId: string; reason: string }>
  pending?: string[]
}

// ---------------------------------------------------------------------------
// Client
// ---------------------------------------------------------------------------

export const ingestApiClient = {
  /** Create a new ingest batch, returns batchId. Optional package context added by GH-252. */
  createBatch: (payload?: BatchCreatePayload): Promise<BatchCreateResponse> =>
    apiClient.post<BatchCreateResponse>('/api/ingest/batches', payload),

  /** Register file metadata with the batch after upload IDs are obtained */
  addBatchItems: (batchId: string, payload: BatchAddItemsPayload): Promise<void> =>
    apiClient.post<void>(`/api/ingest/batches/${batchId}/items`, payload),

  /** Direct file upload (< 100 MB) — returns an uploadId */
  uploadFile: async (file: File): Promise<DirectUploadResponse> => {
    const formData = new FormData()
    formData.append('file', file)

    const res = await fetch(`${API_BASE}/api/uploads`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    })

    if (!res.ok) {
      const data = await res.json().catch(() => null)
      const msg =
        typeof data === 'object' && data !== null && 'error' in data
          ? String((data as Record<string, unknown>).error)
          : res.statusText
      throw new Error(msg)
    }

    // Shim: /api/uploads returns { id, ... }; map to { uploadId, ... }
    const raw = (await res.json()) as { id?: string; uploadId?: string; filename?: string; contentType?: string; sizeBytes?: number }
    return { uploadId: raw.uploadId ?? raw.id ?? '', filename: raw.filename ?? '', contentType: raw.contentType ?? '', sizeBytes: raw.sizeBytes ?? 0 } as DirectUploadResponse
  },

  /** Open an EventSource stream for batch progress events */
  openBatchStream: (batchId: string): EventSource =>
    new EventSource(`${API_BASE}/api/ingest/batches/${batchId}/stream`, {
      withCredentials: true,
    }),

  // OCR Settings admin endpoints
  listOcrSettings: async (): Promise<OcrSettingsListResponse> => {
    // Backend returns full record shape ({id, providerId, label, credentials, priority, ...}).
    // Project to the flat client shape the form/list components expect.
    type RawRow = {
      id: string
      providerId: string
      label: string | null
      credentials: { apiKey: string; endpoint?: string; model?: string; processorPath?: string }
      priority: number
      createdAt: string | number | Date
      updatedAt: string | number | Date
    }
    const raw = await apiClient.get<{ settings: RawRow[] }>('/api/admin/ocr-settings')
    return {
      settings: raw.settings.map((r) => ({
        id: r.id,
        provider: r.providerId,
        label: r.label ?? '',
        apiKey: r.credentials.apiKey,
        isDefault: r.priority === 0,
        endpoint: r.credentials.endpoint,
        model: r.credentials.model,
        processorPath: r.credentials.processorPath,
        createdAt: String(r.createdAt),
        updatedAt: String(r.updatedAt),
      })),
    }
  },

  createOcrSetting: (payload: OcrSettingCreatePayload): Promise<OcrProviderSetting> => {
    // Server contract: { providerId, label?, credentials: { apiKey, endpoint?, model?, processorPath? }, isActive?, priority? }
    const body = {
      providerId: payload.provider,
      ...(payload.label ? { label: payload.label } : {}),
      credentials: {
        apiKey: payload.apiKey,
        ...(payload.endpoint ? { endpoint: payload.endpoint } : {}),
        ...(payload.model ? { model: payload.model } : {}),
        ...(payload.processorPath ? { processorPath: payload.processorPath } : {}),
      },
      isActive: true,
      ...(payload.isDefault ? { priority: 0 } : {}),
    }
    return apiClient.post<OcrProviderSetting>('/api/admin/ocr-settings', body)
  },

  updateOcrSetting: (id: string, payload: OcrSettingUpdatePayload): Promise<OcrProviderSetting> => {
    const body: Record<string, unknown> = {}
    if (payload.label !== undefined) body.label = payload.label
    if (payload.apiKey || payload.endpoint || payload.model || payload.processorPath) {
      body.credentials = {
        ...(payload.apiKey ? { apiKey: payload.apiKey } : {}),
        ...(payload.endpoint ? { endpoint: payload.endpoint } : {}),
        ...(payload.model ? { model: payload.model } : {}),
        ...(payload.processorPath ? { processorPath: payload.processorPath } : {}),
      }
    }
    if (payload.isDefault) body.priority = 0
    return apiClient.put<OcrProviderSetting>(`/api/admin/ocr-settings/${id}`, body)
  },

  deleteOcrSetting: (id: string): Promise<void> =>
    apiClient.delete<void>(`/api/admin/ocr-settings/${id}`),

  // ── P4 review queue endpoints ──────────────────────────────────────────────

  /**
   * GET /api/ingest/batches/:batchId/items
   * Returns paginated + filtered list of ingest items with upload metadata.
   */
  /**
   * Build the item list URL with repeated ?status= params so the server can
   * use IN (...) — comma-joined string caused zero matches (F3 fix).
   */
  getBatchItems: (batchId: string, query?: BatchItemsQuery): Promise<BatchItemsResponse> => {
    const params = new URLSearchParams()
    // Repeated params for multi-value status / errorClass
    for (const s of query?.status ?? []) params.append('status', s)
    for (const ec of query?.errorClass ?? []) params.append('errorClass', ec)
    if (query?.language) params.set('language', query.language)
    if (query?.size) params.set('size', query.size)
    if (query?.search) params.set('search', query.search)
    if (query?.page) params.set('page', String(query.page))
    if (query?.pageSize) params.set('pageSize', String(query.pageSize))
    const qs = params.toString()
    return apiClient.get<BatchItemsResponse>(
      `/api/ingest/batches/${batchId}/items${qs ? `?${qs}` : ''}`,
    )
  },

  /**
   * GET /api/ingest/batches/:batchId/stats
   * Returns aggregated status counts and error-class breakdown.
   */
  getBatchStats: (batchId: string): Promise<BatchStatsResponse> =>
    apiClient.get<BatchStatsResponse>(`/api/ingest/batches/${batchId}/stats`),

  /**
   * POST /api/ingest/batches/:batchId/bulk
   * Executes a bulk action on selected items.
   * Rate-limited: 10 requests/min/user.
   * Returns BulkActionResult — partial failures are in result.failed, NOT thrown.
   */
  bulkAction: (batchId: string, payload: BulkActionPayload): Promise<BulkActionResult> =>
    apiClient.post<BulkActionResult>(`/api/ingest/batches/${batchId}/bulk`, payload),
}
